import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, Search, Car, ArrowLeft, ChevronRight, Tag } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Marcas = () => {
  // Marcas state
  const [marcas, setMarcas] = useState([]);
  const [filteredMarcas, setFilteredMarcas] = useState([]);
  const [loadingMarcas, setLoadingMarcas] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Marca dialog
  const [showMarcaDialog, setShowMarcaDialog] = useState(false);
  const [editingMarcaId, setEditingMarcaId] = useState(null);
  const [marcaFormNombre, setMarcaFormNombre] = useState('');
  const [savingMarca, setSavingMarca] = useState(false);
  const [deleteMarcaId, setDeleteMarcaId] = useState(null);

  // Drill-down: selected marca
  const [selectedMarca, setSelectedMarca] = useState(null);

  // Modelos state (for drill-down view)
  const [modelos, setModelos] = useState([]);
  const [filteredModelos, setFilteredModelos] = useState([]);
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [modeloSearchTerm, setModeloSearchTerm] = useState('');

  // Modelo dialog
  const [showModeloDialog, setShowModeloDialog] = useState(false);
  const [editingModeloId, setEditingModeloId] = useState(null);
  const [modeloFormNombre, setModeloFormNombre] = useState('');
  const [savingModelo, setSavingModelo] = useState(false);
  const [deleteModeloId, setDeleteModeloId] = useState(null);

  // Model counts per marca
  const [modelCounts, setModelCounts] = useState({});

  useEffect(() => {
    loadMarcas();
  }, []);

  useEffect(() => {
    applyMarcaFilters();
  }, [marcas, searchTerm]);

  useEffect(() => {
    applyModeloFilters();
  }, [modelos, modeloSearchTerm]);

  const loadMarcas = async () => {
    try {
      const [marcasRes, modelosRes] = await Promise.all([
        axios.get(`${API}/marcas`),
        axios.get(`${API}/modelos`)
      ]);
      setMarcas(marcasRes.data);

      // Count models per marca
      const counts = {};
      modelosRes.data.forEach(m => {
        counts[m.marcaId] = (counts[m.marcaId] || 0) + 1;
      });
      setModelCounts(counts);
    } catch (error) {
      console.error('Error loading marcas:', error);
      toast.error('Error al cargar marcas');
    } finally {
      setLoadingMarcas(false);
    }
  };

  const loadModelos = async (marcaId) => {
    setLoadingModelos(true);
    try {
      const response = await axios.get(`${API}/marcas/${marcaId}/modelos`);
      setModelos(response.data);
    } catch (error) {
      console.error('Error loading modelos:', error);
      toast.error('Error al cargar modelos');
    } finally {
      setLoadingModelos(false);
    }
  };

  const applyMarcaFilters = () => {
    let filtered = [...marcas];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(m => m.nombre.toLowerCase().includes(term));
    }
    setFilteredMarcas(filtered);
  };

  const applyModeloFilters = () => {
    let filtered = [...modelos];
    if (modeloSearchTerm) {
      const term = modeloSearchTerm.toLowerCase();
      filtered = filtered.filter(m => m.nombre.toLowerCase().includes(term));
    }
    setFilteredModelos(filtered);
  };

  // ========== Marca handlers ==========

  const handleOpenMarcaDialog = (marca = null) => {
    if (marca) {
      setEditingMarcaId(marca.id);
      setMarcaFormNombre(marca.nombre);
    } else {
      setEditingMarcaId(null);
      setMarcaFormNombre('');
    }
    setShowMarcaDialog(true);
  };

  const handleSaveMarca = async () => {
    if (!marcaFormNombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSavingMarca(true);
    try {
      if (editingMarcaId) {
        await axios.put(`${API}/marcas/${editingMarcaId}`, { nombre: marcaFormNombre.trim() });
        toast.success('Marca actualizada');
        // Update selectedMarca if editing the currently selected one
        if (selectedMarca?.id === editingMarcaId) {
          setSelectedMarca(prev => ({ ...prev, nombre: marcaFormNombre.trim() }));
        }
      } else {
        await axios.post(`${API}/marcas`, { nombre: marcaFormNombre.trim() });
        toast.success('Marca creada');
      }
      setShowMarcaDialog(false);
      loadMarcas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar marca');
    } finally {
      setSavingMarca(false);
    }
  };

  const handleDeleteMarca = async (id) => {
    try {
      await axios.delete(`${API}/marcas/${id}`);
      toast.success('Marca eliminada');
      loadMarcas();
      if (selectedMarca?.id === id) {
        setSelectedMarca(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar marca');
    }
    setDeleteMarcaId(null);
  };

  const handleSelectMarca = (marca) => {
    setSelectedMarca(marca);
    setModeloSearchTerm('');
    loadModelos(marca.id);
  };

  const handleBackToMarcas = () => {
    setSelectedMarca(null);
    setModelos([]);
    setModeloSearchTerm('');
  };

  // ========== Modelo handlers ==========

  const handleOpenModeloDialog = (modelo = null) => {
    if (modelo) {
      setEditingModeloId(modelo.id);
      setModeloFormNombre(modelo.nombre);
    } else {
      setEditingModeloId(null);
      setModeloFormNombre('');
    }
    setShowModeloDialog(true);
  };

  const handleSaveModelo = async () => {
    if (!modeloFormNombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSavingModelo(true);
    try {
      if (editingModeloId) {
        await axios.put(`${API}/modelos/${editingModeloId}`, { nombre: modeloFormNombre.trim() });
        toast.success('Modelo actualizado');
      } else {
        await axios.post(`${API}/modelos`, {
          marcaId: selectedMarca.id,
          nombre: modeloFormNombre.trim()
        });
        toast.success('Modelo creado');
      }
      setShowModeloDialog(false);
      loadModelos(selectedMarca.id);
      loadMarcas(); // Refresh counts
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar modelo');
    } finally {
      setSavingModelo(false);
    }
  };

  const handleDeleteModelo = async (id) => {
    try {
      await axios.delete(`${API}/modelos/${id}`);
      toast.success('Modelo eliminado');
      loadModelos(selectedMarca.id);
      loadMarcas(); // Refresh counts
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar modelo');
    }
    setDeleteModeloId(null);
  };

  if (loadingMarcas) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  // ========== DRILL-DOWN: Modelos view ==========
  if (selectedMarca) {
    return (
      <div className="space-y-6 bg-background min-h-screen">
        <div className="bg-background border-b">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
            <Button variant="ghost" className="mb-4" onClick={handleBackToMarcas}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a Marcas
            </Button>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Car className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-semibold tracking-tight">{selectedMarca.nombre}</h1>
                    <p className="text-muted-foreground mt-1">{modelos.length} modelo(s)</p>
                  </div>
                </div>
              </div>
              <Button onClick={() => handleOpenModeloDialog()} data-testid="new-modelo-button">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Modelo
              </Button>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Search */}
          <Card className="p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar modelo..."
                  value={modeloSearchTerm}
                  onChange={(e) => setModeloSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-modelo-input"
                />
              </div>
            </div>
          </Card>

          {loadingModelos ? (
            <div className="flex items-center justify-center min-h-[200px]">
              <div className="text-muted-foreground">Cargando modelos...</div>
            </div>
          ) : filteredModelos.length === 0 ? (
            <Card className="p-12 text-center">
              <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {modelos.length === 0 ? 'No hay modelos para esta marca' : 'No se encontraron resultados'}
              </p>
              {modelos.length === 0 && (
                <Button className="mt-4" onClick={() => handleOpenModeloDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar primer modelo
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {filteredModelos.map((modelo) => (
                <Card key={modelo.id} className="p-4" data-testid="modelo-card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-accent rounded-md">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <span className="font-medium">{modelo.nombre}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenModeloDialog(modelo)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteModeloId(modelo.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Modelo Dialog */}
        <Dialog open={showModeloDialog} onOpenChange={setShowModeloDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{editingModeloId ? 'Editar' : 'Nuevo'} Modelo</DialogTitle>
              <DialogDescription>
                {editingModeloId ? 'Modifica el nombre del modelo' : `Agregar modelo para ${selectedMarca.nombre}`}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="space-y-2">
                <Label htmlFor="modeloNombre">Nombre *</Label>
                <Input
                  id="modeloNombre"
                  value={modeloFormNombre}
                  onChange={(e) => setModeloFormNombre(e.target.value)}
                  placeholder="Ej. Corolla"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveModelo()}
                  data-testid="modelo-nombre-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModeloDialog(false)} disabled={savingModelo}>
                Cancelar
              </Button>
              <Button onClick={handleSaveModelo} disabled={savingModelo} data-testid="save-modelo-button">
                {savingModelo ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modelo Confirm */}
        <AlertDialog open={!!deleteModeloId} onOpenChange={() => setDeleteModeloId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar modelo?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={() => handleDeleteModelo(deleteModeloId)}>
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // ========== MAIN: Marcas list view ==========
  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Marcas</h1>
              <p className="text-muted-foreground mt-1">{marcas.length} marcas registradas</p>
            </div>
            <Button onClick={() => handleOpenMarcaDialog()} data-testid="new-marca-button">
              <Plus className="h-4 w-4 mr-2" />
              Nueva Marca
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Filtros */}
        <Card className="p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar marca..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-marca-input"
              />
            </div>
          </div>
        </Card>

        {filteredMarcas.length === 0 ? (
          <Card className="p-12 text-center">
            <Car className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {marcas.length === 0 ? 'No hay marcas creadas' : 'No se encontraron resultados'}
            </p>
            {marcas.length === 0 && (
              <Button className="mt-4" onClick={() => handleOpenMarcaDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Crear primera marca
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {filteredMarcas.map((marca) => (
              <Card
                key={marca.id}
                className="p-5 cursor-pointer hover:border-primary/50 transition-colors group"
                onClick={() => handleSelectMarca(marca)}
                data-testid="marca-card"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                      <Car className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{marca.nombre}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {modelCounts[marca.id] || 0} modelo(s)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleOpenMarcaDialog(marca); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setDeleteMarcaId(marca.id); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <ChevronRight className="h-5 w-5 text-muted-foreground ml-1" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Marca Dialog */}
      <Dialog open={showMarcaDialog} onOpenChange={setShowMarcaDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingMarcaId ? 'Editar' : 'Nueva'} Marca</DialogTitle>
            <DialogDescription>
              {editingMarcaId ? 'Modifica el nombre de la marca' : 'Ingrese el nombre de la nueva marca'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              <Label htmlFor="marcaNombre">Nombre *</Label>
              <Input
                id="marcaNombre"
                value={marcaFormNombre}
                onChange={(e) => setMarcaFormNombre(e.target.value)}
                placeholder="Ej. Toyota"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSaveMarca()}
                data-testid="marca-nombre-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarcaDialog(false)} disabled={savingMarca}>
              Cancelar
            </Button>
            <Button onClick={handleSaveMarca} disabled={savingMarca} data-testid="save-marca-button">
              {savingMarca ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Marca Confirm */}
      <AlertDialog open={!!deleteMarcaId} onOpenChange={() => setDeleteMarcaId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar marca?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Si la marca tiene modelos asociados, deberá eliminarlos primero.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteMarca(deleteMarcaId)}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Marcas;
